<template>
  <div
    class="carousel-card shrink-0 first:ml-auto last:mr-auto w-80 rounded-12 overflow-visible mt-6 mb-20 shadow-2"
    :class="isFeatured ? 'origin-left scale-105 mr-8 sm:mr-14' : 'mr-4 sm:mr-10'"
  >
    <ScoreSummary
      layout="vertical"
      :score-summary="entitySummary"
      :type="'iso' in props.entity ? 'migration' : 'provider'"
    />
  </div>
</template>

<script lang="ts" setup>
import {
  type GQLEntityScoresProvider,
  type GQLEntityScoresCountry,
} from './generated/entities-scores'
import type { ScoreSummaryScore, ScoreSummaryEntity } from '@core/components/score-summary/types'

const props = defineProps<{
  entity: GQLEntityScoresProvider | GQLEntityScoresCountry
  isFeatured: boolean
}>()

const { $t } = useNuxtApp()

const entityScore: ComputedRef<ScoreSummaryScore | null> = computed(() => {
  if (!props.entity.score) {
    return null
  }

  return {
    value: props.entity.score.value,
    categories: props.entity.score.categories.map((item) => ({
      label: $t(`scores.category.${item.name}`),
      ...item,
    })),
  }
})

const ctaLabel = computed(() => {
  if (!props.isFeatured) {
    return $t('read_full_review')
  }
  if ('iso' in props.entity) {
    return $t('moving_to', { migration: props.entity.labels.label })
  }

  return $t('go_to', { provider: props.entity.displayName })
})

const entitySummary = computed((): ScoreSummaryEntity => {
  if ('iso' in props.entity) {
    return {
      displayName: props.entity.labels.label,
      slug: props.entity.iso,
      url: props.entity.migrationUrl || '',
      cta: {
        url: props.entity.migrationUrl,
        target: '_blank',
        label: ctaLabel.value,
        asLink: !props.isFeatured,
      },
      logo: props.entity.flag,
      score: entityScore.value,
    }
  } else {
    return {
      displayName: props.entity.displayName,
      slug: props.entity.slug,
      url: props.entity.url,
      cta: {
        url: props.isFeatured
          ? (addAffiliateMobileAppParams(props.entity.affiliateUrl) ?? null)
          : props.entity.url,
        target: props.isFeatured ? '_blank' : '_self',
        label: ctaLabel.value,
        asLink: !props.isFeatured,
      },
      logo: props.entity.logo.sm,
      score: entityScore.value,
    }
  }
})
</script>
